import React, { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import SessionQuestionsList from "./SessionQuestionsList";

/**
 * Reusable "Ask the chair" dialog. Drop in anywhere you have a session id.
 *
 * Props:
 *   - sessionId (required): conference_sessions.session_id
 *   - sessionTitle (optional, for header context)
 *   - sessionSpeaker (optional)
 *   - trigger (optional ReactNode) — defaults to a compact button
 *   - testIdPrefix (optional) — for unique data-testids across multiple instances
 */
export default function AskQuestionDialog({
  sessionId,
  sessionTitle,
  sessionSpeaker,
  trigger,
  testIdPrefix = "ask",
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 4) {
      toast.error("Question is too short.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sessions/${sessionId}/questions`, { text: t });
      toast.success("Question sent to the chair — they'll see it live.");
      setText("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? (
        <span onClick={() => setOpen(true)} data-testid={`${testIdPrefix}-open-wrapper`}>{trigger}</span>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full border-slate-300 text-slate-700 hover:text-[#28BBE3]"
          onClick={() => setOpen(true)}
          data-testid={`${testIdPrefix}-open-btn`}
        >
          <MessageSquare size={13} className="mr-1.5" /> Ask a question
        </Button>
      )}
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Ask the chair</DialogTitle>
          {sessionTitle && (
            <DialogDescription className="text-sm text-slate-500">
              {sessionTitle}{sessionSpeaker ? ` · ${sessionSpeaker}` : ""}
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid={`${testIdPrefix}-form`}>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            minLength={4}
            maxLength={600}
            rows={4}
            placeholder="Type your question for the chair…"
            data-testid={`${testIdPrefix}-text-input`}
            className="rounded-2xl"
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Sent only to the chair — your name will be visible to them.</span>
            <span className={text.length > 500 ? "text-rose-600" : ""}>{text.length}/600</span>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <Button
              type="button"
              variant="ghost"
              className="rounded-full text-slate-600"
              onClick={() => setOpen(false)}
              data-testid={`${testIdPrefix}-cancel-btn`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
              data-testid={`${testIdPrefix}-submit-btn`}
            >
              {submitting ? "Sending…" : "Send question"}
            </Button>
          </div>
        </form>

        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2.5">
            Questions so far
          </div>
          <SessionQuestionsList sessionId={sessionId} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
